'use client';

import {
  Box,
  TextField,
  Button,
  Autocomplete,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from '@mui/material';
import { useCallback, useState, type KeyboardEvent } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTheoryActivityFilterStore } from '@/stores/theoryActivityFilterStore';
import { log } from '@/lib/utils/logger';
import type { TheoryActivitySearchCriteria } from '@/api/ApiClient';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { useReopenSearchPanelOnFailure } from '@/hooks/useReopenSearchPanelOnFailure';
import { markNextSearchUserInitiated } from '@/stores/searchUiStore';

const OPTION_KEYS: OptionKey[] = ['synthesisTechniques'];

interface TheoryActivitySearchPanelProps {
  /** Custom title for the search panel */
  title?: string;
  /** Whether this panel is used in a selection dialog (hides action buttons) */
  isSelectionMode?: boolean;
}

export const TheoryActivitySearchPanel = ({ title, isSelectionMode = false }: TheoryActivitySearchPanelProps = {}) => {
  const { setFilters, resetFilters, setPaginationModel, filters: appliedCriteria } = useTheoryActivityFilterStore();
  const { options, loading, error } = useSelectOptions(OPTION_KEYS);

  /** Unsaved edits when not in selection mode. `null` = follow store `appliedCriteria`. Selection mode writes the store on each change (no mirror effect). */
  const [filterDraft, setFilterDraft] = useState<TheoryActivitySearchCriteria | null>(null);

  const localFilters = isSelectionMode ? appliedCriteria : (filterDraft ?? appliedCriteria);

  const flushSelectionCriteria = useCallback(
    (next: TheoryActivitySearchCriteria) => {
      setFilters(next);
      setPaginationModel((prevModel) => ({
        ...prevModel,
        page: 0,
      }));
    },
    [setFilters, setPaginationModel]
  );

  const setLocalFilters = useCallback(
    (next: TheoryActivitySearchCriteria | ((prev: TheoryActivitySearchCriteria) => TheoryActivitySearchCriteria)) => {
      if (isSelectionMode) {
        const base = useTheoryActivityFilterStore.getState().filters;
        const merged =
          typeof next === 'function'
            ? (next as (p: TheoryActivitySearchCriteria) => TheoryActivitySearchCriteria)(base)
            : next;
        flushSelectionCriteria(merged);
        return;
      }
      setFilterDraft((prevDraft) => {
        const base = prevDraft ?? useTheoryActivityFilterStore.getState().filters;
        return typeof next === 'function'
          ? (next as (p: TheoryActivitySearchCriteria) => TheoryActivitySearchCriteria)(base)
          : next;
      });
    },
    [isSelectionMode, flushSelectionCriteria]
  );

  const [isExpanded, setIsExpanded] = useState(true);
  useReopenSearchPanelOnFailure(setIsExpanded);

  const handleApply = useCallback(() => {
    markNextSearchUserInitiated();
    log.debug('[TheoryActivitySearchPanel] Applying filters:', localFilters);
    setFilters(localFilters);
    setFilterDraft(null);
    setPaginationModel((prevModel) => ({
      ...prevModel,
      page: 0,
    }));
    setIsExpanded(false);
  }, [localFilters, setFilters, setPaginationModel]);

  const handleReset = useCallback(() => {
    resetFilters();
    setFilterDraft(null);
  }, [resetFilters]);

  const handleSearchOnEnter = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      handleApply();
    },
    [handleApply]
  );

  if (loading) {
    return (
      <Box sx={{ width: '98%', px: 2, py: 1 }}>
        <Typography variant="body1">Loading filter options...</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box sx={{ width: '98%', px: 2, py: 1 }}>
        <Typography variant="body1">Error loading filter options...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', px: isSelectionMode ? 2 : 2, py: isSelectionMode ? 1 : 1 }}>
      <Box display="flex" alignItems="center" gap={2} sx={{ mt: isSelectionMode ? 0 : 2, mb: 1 }}>
        <Typography variant="h5" fontWeight={600}>
          {title || 'Search Theory Activities'}
        </Typography>
      </Box>

      <Accordion
        sx={{ width: '100%', mb: isSelectionMode ? 1 : 2 }}
        expanded={isExpanded}
        onChange={() => setIsExpanded(!isExpanded)}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1a-content" id="panel1a-header">
          <Typography fontWeight={600}>Filter Theory Activities</Typography>
        </AccordionSummary>
        <AccordionDetails onKeyDown={handleSearchOnEnter}>
          {/* First Row: General search and Technique */}
          <Box
            display="flex"
            flexWrap="wrap"
            gap={isSelectionMode ? 1 : 2}
            mb={isSelectionMode ? 2 : 3}
            alignItems="center"
          >
            <TextField
              label="Search Term"
              variant="outlined"
              size="small"
              value={localFilters.searchTerm || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, searchTerm: e.target.value })}
              sx={{ minWidth: 300 }}
              placeholder="Search in dataset, project, description..."
            />

            {/* Technique dropdown */}
            <Autocomplete
              size="small"
              sx={{ minWidth: 200 }}
              options={options.synthesisTechniques ?? []}
              getOptionLabel={(option) => option?.name ?? ''}
              value={options.synthesisTechniques?.find((t) => t.id === localFilters.techniqueId) ?? null}
              onChange={(_, newValue) => setLocalFilters({ ...localFilters, techniqueId: newValue?.id ?? undefined })}
              renderInput={(params) => <TextField {...params} label="Technique" />}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />

            <TextField
              label="Software"
              variant="outlined"
              size="small"
              value={localFilters.software || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, software: e.target.value })}
              sx={{ minWidth: 150 }}
            />

            <TextField
              label="Version"
              variant="outlined"
              size="small"
              value={localFilters.softwareVersion || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, softwareVersion: e.target.value })}
              sx={{ minWidth: 120 }}
            />
          </Box>

          {/* Second Row: Date range */}
          <Box
            display="flex"
            flexWrap="wrap"
            gap={isSelectionMode ? 1 : 2}
            mb={isSelectionMode ? 2 : 3}
            alignItems="center"
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date After"
                value={localFilters.dateAfter || null}
                onChange={(newValue) => setLocalFilters({ ...localFilters, dateAfter: newValue || undefined })}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
              />
              <DatePicker
                label="Date Before"
                value={localFilters.dateBefore || null}
                onChange={(newValue) => setLocalFilters({ ...localFilters, dateBefore: newValue || undefined })}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
              />
            </LocalizationProvider>

            <TextField
              label="Project ID"
              variant="outlined"
              size="small"
              value={localFilters.projectId || ''}
              onChange={(e) => setLocalFilters({ ...localFilters, projectId: e.target.value })}
              sx={{ minWidth: 150 }}
            />
          </Box>

          <Box
            display="flex"
            justifyContent="flex-end"
            gap={2}
            flexWrap="wrap"
            sx={{ mt: isSelectionMode ? 2 : 3, mb: isSelectionMode ? 2 : 3 }}
          >
            <Button variant="contained" onClick={handleApply} sx={{ width: 100 }}>
              Search
            </Button>
            <Button variant="outlined" onClick={handleReset} sx={{ width: 100 }} color="error">
              Reset
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
